import React, {
  type FC,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useState
} from 'react';
import classnames from 'classnames';
import {
  ButtonBack,
  ButtonNext,
  CarouselContext,
  CarouselProvider
} from 'pure-react-carousel';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';

import styles from 'theme/pages/wise/WiseProduct/components/WiseCarousel/WiseCarousel.module.scss';

const WiseCarouselEdgeFades: FC<{
  totalSlides: number;
  visibleSlides: number;
}> = ({ totalSlides: totalSlidesProp, visibleSlides: visibleSlidesProp }) => {
  const carouselStore = useContext(CarouselContext);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useLayoutEffect(() => {
    if (!carouselStore) {
      return undefined;
    }

    const sync = () => {
      const s = carouselStore.getStoreState();
      const total = s.totalSlides;
      const visible = Math.min(Math.max(1, s.visibleSlides), total);
      const maxStart = Math.max(0, total - visible);
      setShowLeft(s.currentSlide > 0);
      setShowRight(s.currentSlide < maxStart);
    };

    sync();
    carouselStore.subscribe(sync);
    return () => {
      carouselStore.unsubscribe(sync);
    };
  }, [carouselStore, totalSlidesProp, visibleSlidesProp]);

  return (
    <>
      <div
        className={classnames(
          styles.edgeFade,
          styles.edgeFadeLeft,
          showLeft && styles.edgeFadeVisible
        )}
        aria-hidden
      />
      <div
        className={classnames(
          styles.edgeFade,
          styles.edgeFadeRight,
          showRight && styles.edgeFadeVisible
        )}
        aria-hidden
      />
    </>
  );
};

export type WiseCarouselTab = {
  id: string;
  label: ReactNode;
};

export type WiseCarouselProps = {
  totalSlides: number;
  visibleSlides: number;
  naturalSlideWidth?: number;
  naturalSlideHeight?: number;
  isIntrinsicHeight?: boolean;
  disableAnimation?: boolean;
  disableKeyboard?: boolean;
  title?: ReactNode;
  tabs?: WiseCarouselTab[];
  activeTabId?: string;
  onTabChange?: (id: string) => void;
  showNavArrows?: boolean;
  headerAction?: ReactNode;
  children: ReactNode;
  className?: string;
};

const WiseCarousel: FC<WiseCarouselProps> = ({
  totalSlides,
  visibleSlides,
  naturalSlideWidth = 0,
  naturalSlideHeight = 0,
  isIntrinsicHeight: isIntrinsicHeightProp,
  disableAnimation,
  disableKeyboard,
  title,
  tabs,
  activeTabId,
  onTabChange,
  showNavArrows = true,
  headerAction,
  children,
  className
}) => {
  if (totalSlides < 1) {
    return null;
  }

  const isIntrinsicHeight = isIntrinsicHeightProp ?? true;

  const hasTabs = Boolean(tabs?.length);
  const hasTitle = Boolean(title);
  const hasLeft = hasTabs || hasTitle;
  const hasRight = showNavArrows || Boolean(headerAction);
  const showHeader = hasLeft || hasRight;

  return (
    <CarouselProvider
      naturalSlideWidth={naturalSlideWidth}
      naturalSlideHeight={naturalSlideHeight}
      totalSlides={totalSlides}
      visibleSlides={visibleSlides}
      isIntrinsicHeight={isIntrinsicHeight}
      disableAnimation={disableAnimation}
      disableKeyboard={disableKeyboard}
      className={classnames(
        styles.provider,
        className,
        'StylePath-Pages-Wise-WiseProduct-components-WiseCarousel'
      )}>
      <div className={styles.root}>
        {showHeader && (
          <div
            className={classnames(styles.header, !hasLeft && styles.headerOnlyActions)}
            role={hasTabs ? 'tablist' : undefined}>
            <div className={styles.headerMain}>
              {hasTabs ? (
                <div className={styles.tabs}>
                  {tabs!.map((tab) => {
                    const isActive = tab.id === activeTabId;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={classnames(styles.tab, isActive && styles.tabActive)}
                        onClick={() => onTabChange?.(tab.id)}>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              ) : hasTitle ? (
                <div className={styles.title}>{title}</div>
              ) : null}
            </div>
            {hasRight && (
              <div className={styles.headerActions}>
                {showNavArrows && (
                  <>
                    <ButtonBack className={classnames(styles.navButton, 'wise-carousel-nav-back')}>
                      <ChevronLeft aria-hidden />
                    </ButtonBack>
                    <ButtonNext className={classnames(styles.navButton, 'wise-carousel-nav-next')}>
                      <ChevronRight aria-hidden />
                    </ButtonNext>
                  </>
                )}
                {headerAction ? <div className={styles.actionSlot}>{headerAction}</div> : null}
              </div>
            )}
          </div>
        )}
        <div className={styles.slider}>
          {children}
          <WiseCarouselEdgeFades totalSlides={totalSlides} visibleSlides={visibleSlides} />
        </div>
      </div>
    </CarouselProvider>
  );
};

export default WiseCarousel;
